import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';

export interface DatasetExportFile {
  id: number;
  name: string;
  type: number;
  round_number: number | null;
  source_session_id: number | null;
  url: string;
}

export interface DatasetExport {
  files: DatasetExportFile[];
  total: number;
}

/**
 * Signed URLs for every file in a model's dataset, in one call.
 *
 * Per-file download is one request per object, and a dataset built from
 * contributed clips is two files per clip — a 200-clip model is 400 round
 * trips. This is the "get your data out before the model goes" action, so it
 * has to work at that size.
 */
export const useExportCustomModelDataset = () =>
  useMutation<DatasetExport, AxiosError<BaseError>, number>(async modelId => {
    const { data } = await Axios.get<DatasetExport>(
      GET_URL(modelId.toString()).customModelDatasetExport
    );
    return data;
  });

/**
 * Saves every file in the export. Browsers throttle simultaneous downloads, so
 * the anchors are triggered in sequence with a short gap rather than all at
 * once, which silently drops most of them.
 */
export const downloadDatasetExport = async (
  files: DatasetExportFile[]
): Promise<void> => {
  for (const file of files) {
    if (!file.url) continue;
    const anchor = document.createElement('a');
    anchor.href = file.url;
    anchor.download = file.name;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    await new Promise(resolve => setTimeout(resolve, 150));
  }
};
